/**
 * Renders a QR code as an inline SVG using qrcode-generator. Keeps the
 * bundle tiny — SVG is rasterized by the browser, no canvas round-trip.
 *
 * qrcode-generator is a ~15 KB library with no deps; we pick correction
 * level H (30% redundancy) for reliable scanning from across a room.
 */

import qrcode from 'qrcode-generator';

export interface QrOptions {
  /** SVG size in pixels. Default 320. */
  readonly size?: number;
  /** Module color (dark modules). Default #000000. */
  readonly darkColor?: string;
  /** Background color (light modules). Default #ffffff. */
  readonly lightColor?: string;
}

/**
 * Build an SVG string for the given URL. Caller is responsible for
 * injecting into the DOM; the returned string is safe to assign to
 * innerHTML (no user-controlled input reaches attributes).
 */
export function renderQrSvg(text: string, opts: QrOptions = {}): string {
  const size = opts.size ?? 320;
  const dark = opts.darkColor ?? '#000000';
  const light = opts.lightColor ?? '#ffffff';

  const qr = qrcode(0, 'H');
  qr.addData(text);
  qr.make();

  const moduleCount = qr.getModuleCount();
  const cell = size / moduleCount;

  const rects: string[] = [];
  for (let r = 0; r < moduleCount; r++) {
    for (let c = 0; c < moduleCount; c++) {
      if (qr.isDark(r, c)) {
        rects.push(
          `<rect x="${(c * cell).toFixed(3)}" y="${(r * cell).toFixed(
            3
          )}" width="${cell.toFixed(3)}" height="${cell.toFixed(3)}"/>`
        );
      }
    }
  }

  return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges" role="img" aria-label="QR code">
      <rect width="${size}" height="${size}" fill="${light}"/>
      <g fill="${dark}">${rects.join('')}</g>
    </svg>
  `;
}
